import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, CardMedia, Typography } from '@mui/material';

type ProfilePhoto = {
  id: number;
  file_name?: string;
};

type RotatingProfile = {
  id: number;
  profile_id?: string;
  first_name: string;
  last_name: string;
  gender?: string;
  date_of_birth?: string;
  age?: number;
  height_cm?: number;
  city?: string;
  state?: string;
  country?: string;
  caste?: string;
  marital_status?: string;
  work_location?: string;
  education?: string;
  occupation?: string;
  religion?: string;
  photos?: ProfilePhoto[];
};

type RotatingProfileCardProps = {
  profile: RotatingProfile;
  mediaOrigin: string;
  onView: () => void;
  primaryActionLabel: string;
  onPrimaryAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  displayReligion?: boolean;
  compact?: boolean;
};

const ROTATION_MS = 2400;

const formatHeight = (heightCm?: number) => {
  if (!heightCm) return '-';
  const totalInches = Math.round(heightCm / 2.54);
  return `${Math.floor(totalInches / 12)}'${totalInches % 12}"`;
};

const formatDate = (dateValue?: string) => {
  if (!dateValue) return '-';
  const [year, month, day] = dateValue.slice(0, 10).split('-');
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsed.getTime()) ? '-' : new Intl.DateTimeFormat('en-GB').format(parsed);
};

const RotatingProfileCard: React.FC<RotatingProfileCardProps> = ({
  profile,
  mediaOrigin,
  onView,
  onPrimaryAction,
  primaryActionLabel,
  secondaryActionLabel,
  onSecondaryAction,
  displayReligion = true,
  compact = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const photos = useMemo(() => (profile.photos || []).slice(0, 5), [profile.photos]);

  useEffect(() => {
    setCurrentPhotoIndex(0);
  }, [profile.id]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsVisible(Boolean(entry?.isIntersecting));
      },
      { threshold: 0.35 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || photos.length <= 1) return;

    const timer = window.setInterval(() => {
      setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
    }, ROTATION_MS);

    return () => window.clearInterval(timer);
  }, [isVisible, photos.length]);

  const currentPhoto = photos[currentPhotoIndex];
  const photoSrc = currentPhoto ? `${mediaOrigin}/api/profiles/photos/${currentPhoto.id}` : '';

  return (
    <Card
      ref={containerRef}
      sx={{
        height: '100%',
        borderRadius: 2.2,
        border: '1px solid #eadbc8',
        boxShadow: '0 6px 18px rgba(78, 0, 11, 0.08)',
        overflow: 'hidden',
        background: '#fffdfa',
      }}
    >
      <Box sx={{ position: 'relative', backgroundColor: '#f3d6c5', height: compact ? 136 : 164 }}>
        {photoSrc ? (
          <CardMedia
            component="img"
            image={photoSrc}
            alt={`${profile.first_name} ${profile.last_name}`}
            sx={{ height: '100%', objectFit: 'contain', transition: 'opacity 220ms ease', backgroundColor: '#f3d6c5' }}
          />
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f4dcc8 0%, #edd0b2 100%)',
            }}
          >
            <Typography sx={{ color: '#7b0011', fontWeight: 700, letterSpacing: 0.6 }}>No Photo</Typography>
          </Box>
        )}

        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            backgroundColor: '#edf9ea',
            border: '1px solid #9ad09e',
            color: '#2f7d32',
            borderRadius: '20px',
            px: 0.9,
            py: 0.25,
            fontSize: '0.68rem',
            fontWeight: 700,
          }}
        >
          Verified
        </Box>

        {photos.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', gap: 0.5 }}>
            {photos.map((photo, index) => (
              <Box
                key={`${profile.id}-${photo.id}`}
                sx={{
                  width: index === currentPhotoIndex ? 16 : 6,
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: index === currentPhotoIndex ? '#7b0011' : 'rgba(255,255,255,0.85)',
                  transition: 'all 180ms ease',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <CardContent sx={{ p: 1.25 }}>
        <Typography sx={{ color: '#7b0011', fontWeight: 800, fontSize: '1.02rem', lineHeight: 1.2 }}>
          {profile.first_name} {profile.last_name}
        </Typography>
        <Typography sx={{ color: '#6a5f5a', fontSize: '0.74rem', mt: 0.2, fontWeight: 700 }}>
          {profile.profile_id || `Profile ${profile.id}`}
        </Typography>
        <Box sx={{ mt: 0.65, display: 'grid', gap: 0.25 }}>
          <Typography sx={{ color: '#5f5753', fontSize: '0.76rem', lineHeight: 1.25, overflowWrap: 'anywhere' }}>
            {[profile.age ? `${profile.age} years` : '', profile.gender, profile.marital_status].filter(Boolean).join(', ')}
          </Typography>
          <Typography sx={{ color: '#5f5753', fontSize: '0.76rem', lineHeight: 1.25, overflowWrap: 'anywhere' }}>
            {[profile.caste, profile.education, profile.occupation].filter(Boolean).join(', ')}
          </Typography>
          <Typography sx={{ color: '#5f5753', fontSize: '0.76rem', lineHeight: 1.25, overflowWrap: 'anywhere' }}>
            {profile.work_location ? `Work Location: ${profile.work_location}` : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: onSecondaryAction ? '1fr 1fr' : '1fr', gap: 0.75, mt: 1.05 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ borderColor: '#7b0011', color: '#7b0011', fontSize: '0.72rem' }}
            onClick={onView}
          >
            View Profile
          </Button>
          {onSecondaryAction && secondaryActionLabel && (
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: '#7b0011', fontSize: '0.72rem', '&:hover': { backgroundColor: '#5f000e' } }}
              onClick={onSecondaryAction}
            >
              {secondaryActionLabel}
            </Button>
          )}
          {!onSecondaryAction && onPrimaryAction && (
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: '#7b0011', fontSize: '0.72rem', '&:hover': { backgroundColor: '#5f000e' } }}
              onClick={onPrimaryAction}
            >
              {primaryActionLabel}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RotatingProfileCard;